import { useCallback, useState } from 'react';
import { useDataProvider } from 'react-admin';
import { useDispatch } from 'react-redux';

import useCustomNotify from 'hooks/useCustomNotify';
import { openIdentityVerificationDialog } from 'store/actions/identityVerificationActions';
import { KYCStatus, KycStatusResponse } from 'services/types/kycStatus';

const isKycActionBlocked = (data?: Partial<KycStatusResponse>) => {
  const status = data?.status as KYCStatus | undefined;
  const secondsLeft = typeof data?.secondsLeft === 'number' ? data.secondsLeft : null;

  return Boolean(status && status !== KYCStatus.COMPLETED && secondsLeft !== null);
};

const useKycActionGuard = () => {
  const dataProvider = useDataProvider();
  const dispatch = useDispatch();
  const notify = useCustomNotify();
  const [isCheckingKyc, setIsCheckingKyc] = useState(false);

  const blockActionIfKycRequired = useCallback(async () => {
    setIsCheckingKyc(true);

    try {
      const { data } = await dataProvider.get('kycStatus');

      if (isKycActionBlocked(data)) {
        dispatch(openIdentityVerificationDialog({ variant: 'required' }));
        return true;
      }

      return false;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unable to verify identity status';
      notify(errorMessage, 'error');
      return true;
    } finally {
      setIsCheckingKyc(false);
    }
  }, [dataProvider, dispatch, notify]);

  return {
    blockActionIfKycRequired,
    isCheckingKyc,
  };
};

export default useKycActionGuard;
